import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { initializeFirestore, doc, getDoc, setDoc, addDoc, collection, updateDoc, getDocs, query, collectionGroup } from 'firebase/firestore';
import { getAnalytics } from 'firebase/analytics';
import { collection as collection$1 } from 'rxfire/firestore';
import { Observable, timeout } from 'rxjs';
import { getDatabase, ref, set, remove, get, runTransaction, onDisconnect, increment, onValue } from 'firebase/database';

// src/logger.ts
var LogLevel = /* @__PURE__ */ ((LogLevel2) => {
  LogLevel2[LogLevel2["DEBUG"] = 0] = "DEBUG";
  LogLevel2[LogLevel2["INFO"] = 1] = "INFO";
  LogLevel2[LogLevel2["WARN"] = 2] = "WARN";
  LogLevel2[LogLevel2["ERROR"] = 3] = "ERROR";
  LogLevel2[LogLevel2["NONE"] = 4] = "NONE";
  return LogLevel2;
})(LogLevel || {});
var Logger = class {
  config;
  constructor() {
    const isDevelopment = process.env.NODE_ENV === "development";
    const isLocalhost = typeof window !== "undefined" && window.location.hostname === "localhost";
    this.config = {
      level: isDevelopment || isLocalhost ? 0 /* DEBUG */ : 2 /* WARN */,
      enableConsole: isDevelopment || isLocalhost,
      enableSentry: process.env.NODE_ENV === "production"
    };
  }
  /** Updates logger configuration. */
  configure(config) {
    this.config = { ...this.config, ...config };
  }
  /** Gets current log level. */
  getLevel() {
    return this.config.level;
  }
  /** Sets minimum log level. */
  setLevel(level) {
    this.config.level = level;
  }
  /** Debug level logging — detailed diagnostics, dev only. */
  debug(message, ...args) {
    if (this.config.level <= 0 /* DEBUG */ && this.config.enableConsole) {
      console.debug(`[DEBUG ${Date.now()}] ${message}`, ...args);
    }
  }
  /** Info level logging — general informational messages. */
  info(message, ...args) {
    if (this.config.level <= 1 /* INFO */ && this.config.enableConsole) {
      console.info(`[INFO ${Date.now()}] ${message}`, ...args);
    }
  }
  /** Warning level logging — potentially harmful situations. */
  warn(message, ...args) {
    if (this.config.level <= 2 /* WARN */ && this.config.enableConsole) {
      console.warn(`[WARN ${Date.now()}] ${message}`, ...args);
    }
  }
  /**
   * Error level logging — error events. Also sends to Sentry in production if
   * enabled.
   */
  error(message, error, ...args) {
    if (this.config.level <= 3 /* ERROR */ && this.config.enableConsole) {
      console.error(`[ERROR ${Date.now()}] ${message}`, error, ...args);
    }
    if (this.config.enableSentry && error) {
      try {
        import('@sentry/react').then((Sentry) => {
          if (error instanceof Error) {
            Sentry.captureException(error, {
              tags: {
                source: "logger",
                namespace: message.match(/\[(\w+)\]/)?.[1] || "unknown"
              },
              extra: {
                message,
                args,
                timestamp: (/* @__PURE__ */ new Date()).toISOString(),
                userAgent: typeof navigator !== "undefined" ? navigator.userAgent : void 0
              },
              level: "error"
            });
          } else if (error) {
            Sentry.captureMessage(message, {
              level: "error",
              tags: {
                source: "logger",
                namespace: message.match(/\[(\w+)\]/)?.[1] || "unknown",
                error_type: typeof error
              },
              extra: {
                error: String(error),
                args,
                timestamp: (/* @__PURE__ */ new Date()).toISOString()
              }
            });
          } else {
            Sentry.captureMessage(message, {
              level: "error",
              tags: {
                source: "logger",
                namespace: message.match(/\[(\w+)\]/)?.[1] || "unknown"
              },
              extra: { args }
            });
          }
        });
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.error("Failed to send error to Sentry:", e);
        }
      }
    }
  }
  /** Trace level logging with stack trace — dev only. */
  trace(message, ...args) {
    if (this.config.level <= 0 /* DEBUG */ && this.config.enableConsole) {
      console.trace(`[TRACE] ${message}`, ...args);
    }
  }
  /** Group related logs together (collapsed by default). */
  group(label, collapsed = true) {
    if (this.config.enableConsole) {
      if (collapsed) {
        console.groupCollapsed(label);
      } else {
        console.group(label);
      }
    }
  }
  /** End log group. */
  groupEnd() {
    if (this.config.enableConsole) {
      console.groupEnd();
    }
  }
  /** Time a block of code execution. */
  time(label) {
    if (this.config.enableConsole) {
      console.time(label);
    }
  }
  /** End timing a block of code. */
  timeEnd(label) {
    if (this.config.enableConsole) {
      console.timeEnd(label);
    }
  }
  /**
   * Creates a namespaced logger for a specific module.
   * Usage: const log = logger.namespace('InteractionService');
   */
  namespace(name) {
    return {
      debug: (message, ...args) => this.debug(`[${name}] ${message}`, ...args),
      info: (message, ...args) => this.info(`[${name}] ${message}`, ...args),
      warn: (message, ...args) => this.warn(`[${name}] ${message}`, ...args),
      error: (message, error, ...args) => this.error(`[${name}] ${message}`, error, ...args),
      trace: (message, ...args) => this.trace(`[${name}] ${message}`, ...args)
    };
  }
};
var logger = new Logger();
var serviceLogger = logger.namespace("Service");
var contextLogger = logger.namespace("Context");
var componentLogger = logger.namespace("Component");
var handles;
function initFirebase(config, options = {}) {
  if (handles) {
    return handles;
  }
  const app = initializeApp(config);
  const auth = getAuth(app);
  setPersistence(auth, browserLocalPersistence).catch((error) => {
    logger.error("Failed to set auth persistence", error);
  });
  const db = initializeFirestore(app, {
    experimentalAutoDetectLongPolling: true
  });
  const enableAnalytics = options.analytics ?? Boolean(config.measurementId);
  const analytics = enableAnalytics ? getAnalytics(app) : void 0;
  handles = { app, auth, db, analytics };
  return handles;
}
function getDb() {
  if (!handles) {
    throw new Error("Firebase not initialized: call initFirebase(config) before using Firestore.");
  }
  return handles.db;
}
function getFirebaseAuth() {
  if (!handles) {
    throw new Error("Firebase not initialized: call initFirebase(config) before using Auth.");
  }
  return handles.auth;
}
var FirebaseService = class _FirebaseService {
  static instance;
  log = logger.namespace("FirebaseService");
  constructor() {
  }
  /** Gets the singleton instance of FirebaseService. */
  static getInstance() {
    if (!_FirebaseService.instance) {
      _FirebaseService.instance = new _FirebaseService();
    }
    return _FirebaseService.instance;
  }
  /** Get the Firestore database instance. */
  getDatabase() {
    return getDb();
  }
  // === GENERIC CRUD OPERATIONS ===
  /** Get a single document from Firestore. */
  async getDocument(collectionPath, docId) {
    const docRef = doc(getDb(), collectionPath, docId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  }
  /** Set/create a document in Firestore. */
  async setDocument(collectionPath, docId, data) {
    const docRef = doc(getDb(), collectionPath, docId);
    await setDoc(docRef, data);
  }
  /** Add a new document to a collection (with auto-generated ID). */
  async addDocument(collectionPath, data) {
    const docRef = await addDoc(collection(getDb(), collectionPath), data);
    return docRef.id;
  }
  /** Update a document in Firestore. */
  async updateDocument(collectionPath, docId, data) {
    const docRef = doc(getDb(), collectionPath, docId);
    await updateDoc(docRef, data);
  }
  /** Get multiple documents from a collection. */
  async getCollection(collectionPath) {
    const querySnapshot = await getDocs(collection(getDb(), collectionPath));
    return querySnapshot.docs.map((d) => {
      const data = d.data() || {};
      return {
        id: d.id,
        ...data
      };
    });
  }
  /** Get documents with complex queries (for dialogue operations). */
  async queryDocuments(collectionPath, queryRef) {
    const q = queryRef || collection(getDb(), collectionPath);
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((d) => {
      const data = d.data() || {};
      return {
        id: d.id,
        ...data
      };
    });
  }
  /** Query a collection group. */
  async queryCollectionGroup(collectionId, queryConstraints = []) {
    const q = query(collectionGroup(getDb(), collectionId), ...queryConstraints);
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((d) => {
      const data = d.data() || {};
      return {
        id: d.id,
        ref: d.ref,
        ...data
      };
    });
  }
  /** Execute multiple queries in parallel — useful for dialogue operations. */
  async parallelQueries(queries) {
    return Promise.all(queries);
  }
  getCollectionRef(path, baseRef) {
    if (!path || path.length === 0) {
      throw new Error("Path array cannot be empty");
    }
    if (path.some((segment) => segment === void 0 || segment === null)) {
      throw new Error(`Path contains undefined/null values: ${JSON.stringify(path)}`);
    }
    if (!baseRef) {
      if (path.length === 1) {
        return collection(getDb(), path[0]);
      } else {
        baseRef = collection(getDb(), path[0]);
        return this.getCollectionRef(path.slice(1), baseRef);
      }
    } else {
      if (path.length < 2) {
        throw new Error("Need at least 2 path segments for nested collection: doc/collection");
      }
      const docRef = doc(baseRef, path[0]);
      const newCollectionRef = collection(docRef, path[1]);
      if (path.length > 2) {
        return this.getCollectionRef(path.slice(2), newCollectionRef);
      } else {
        return newCollectionRef;
      }
    }
  }
  getDocumentRef(path) {
    if (!path || path.length < 2) {
      throw new Error("Path must contain at least [collection, docId]");
    }
    if (path.length % 2 !== 0) {
      throw new Error("Document path must have even length: collection/doc/(collection/doc/...)");
    }
    let ref2 = doc(getDb(), path[0], path[1]);
    for (let i = 2; i < path.length; i += 2) {
      ref2 = doc(ref2, path[i], path[i + 1]);
    }
    return ref2;
  }
  ListenToCollectionChanges(collectionPath, callback) {
    const colRef = collection(getDb(), collectionPath);
    const subscription = collection$1(colRef).subscribe({
      next: (docs) => {
        const data = docs.map((d) => {
          const docData = d.data();
          const docId = d.id;
          return {
            ...docData,
            id: docId
            // Always use document ID as source of truth
          };
        });
        callback(data);
      },
      error: (error) => {
        console.error("[Firebase] *** RxJS COLLECTION ERROR ***", error);
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }
};
var RealtimeDatabaseService = class _RealtimeDatabaseService {
  /** Global concurrent app sessions (tabs); separate from registration caps and from `/presence/...`. */
  static GLOBAL_SESSION_COUNT_PATH = "/stats/current_session_count";
  static instance;
  static log = logger.namespace("RealtimeDatabaseService");
  /** Resolved lazily so `initFirebase` is guaranteed to have run first. */
  get database() {
    return getDatabase();
  }
  /**
   * Sanitizes a user ID for use in Firebase Realtime Database paths.
   * RTDB paths cannot contain ".", "#", "$", "[", or "]"
   */
  sanitizeUidForRtdb(uid) {
    return uid.replace(/\./g, "_DOT_").replace(/#/g, "_HASH_").replace(/\$/g, "_DOLLAR_").replace(/\[/g, "_LBRACKET_").replace(/\]/g, "_RBRACKET_");
  }
  static getInstance() {
    if (!_RealtimeDatabaseService.instance) {
      _RealtimeDatabaseService.instance = new _RealtimeDatabaseService();
      _RealtimeDatabaseService.instance.cleanupOldWebhookEntries().catch((error) => this.log.warn("Failed to cleanup old webhook entries on init", error));
    }
    return _RealtimeDatabaseService.instance;
  }
  async writeData(path, data) {
    const node = ref(this.database, path);
    await set(node, data);
  }
  async writeLastInteraction(uid, profileId, lastInteraction) {
    const path = `/presence/${uid}/${profileId}/last_interaction/`;
    await this.writeData(path, lastInteraction);
  }
  async removeData(path) {
    const node = ref(this.database, path);
    await remove(node);
  }
  async readLastInteraction(uid, profileId) {
    const path = `/presence/${uid}/${profileId}/last_interaction/`;
    const data = await this.readData(path);
    return data ? {
      type: data.type,
      timestamp: data.timestamp
    } : null;
  }
  async readData(path) {
    const node = ref(this.database, path);
    const snapshot = await get(node);
    return snapshot.exists() ? snapshot.val() : null;
  }
  async updateDataTransaction(path, updateFn) {
    const node = ref(this.database, path);
    await runTransaction(node, updateFn);
  }
  async incrementValue(path, amount = 1) {
    const node = ref(this.database, path);
    await runTransaction(node, (current) => {
      return (current || 0) + amount;
    });
  }
  /**
   * Queue a server-side -1 on this tab's RTDB connection ending (tab close, refresh, sleep, etc.).
   * Call once per logical session before attempting the matching +1 (plan: pair with connect path).
   */
  async prepareGlobalSessionCountOnDisconnect() {
    const node = ref(this.database, _RealtimeDatabaseService.GLOBAL_SESSION_COUNT_PATH);
    await onDisconnect(node).set(increment(-1));
  }
  /**
   * Atomically increment the global session counter if it is strictly below maxConcurrentSessions.
   * @param maxConcurrentSessions cap; if zero or negative, treated as unlimited (no-op, returns true).
   * @returns whether the transaction committed (false = at or over cap).
   */
  async tryIncrementGlobalSessionCount(maxConcurrentSessions) {
    if (maxConcurrentSessions <= 0) {
      return true;
    }
    const node = ref(this.database, _RealtimeDatabaseService.GLOBAL_SESSION_COUNT_PATH);
    const { committed } = await runTransaction(node, (current) => {
      const n = typeof current === "number" && !Number.isNaN(current) ? current : 0;
      if (n >= maxConcurrentSessions) {
        return void 0;
      }
      return n + 1;
    });
    return committed;
  }
  /**
   * Wires `.info/connected`: when true, runs {@link prepareGlobalSessionCountOnDisconnect} then
   * {@link tryIncrementGlobalSessionCount} at most once per attachment (avoids double +1 on reconnect flaps).
   * Returns a dispose function (call on unmount / sign-out).
   */
  attachGlobalSessionCountOnConnected(maxConcurrentSessions, handlers) {
    if (maxConcurrentSessions <= 0) {
      return () => {
      };
    }
    let acquired = false;
    let disposed = false;
    const connectedRef = ref(this.database, ".info/connected");
    const unsubscribe = onValue(connectedRef, async (snap) => {
      if (disposed) {
        return;
      }
      if (!snap.val()) {
        acquired = false;
        return;
      }
      if (acquired) {
        return;
      }
      try {
        await this.prepareGlobalSessionCountOnDisconnect();
        const ok = await this.tryIncrementGlobalSessionCount(maxConcurrentSessions);
        if (ok) {
          acquired = true;
        } else {
          handlers?.onDenied?.();
        }
      } catch (e) {
        _RealtimeDatabaseService.log.warn("Global session count attach failed", e);
      }
    });
    return () => {
      disposed = true;
      unsubscribe();
    };
  }
  listenToData(path, callback) {
    const node = ref(this.database, path);
    const unsubscribe = onValue(node, (snapshot) => {
      const data = snapshot.exists() ? snapshot.val() : null;
      callback(data);
    });
    return unsubscribe;
  }
  /**
   * Listen for BFL webhook updates for specific image IDs.
   * Returns an Observable that emits BflImageResult format when webhook data is received.
   */
  listenForWebhookUpdates$(imageIds, originalRequests, originalPrompts) {
    return new Observable((subscriber) => {
      const listeners = [];
      const receivedUpdates = [];
      console.log(`[RealtimeDB] Setting up listeners for ${imageIds.length} image IDs:`, imageIds);
      imageIds.forEach((imageId) => {
        const webhookRef = ref(this.database, `/bfl-webhooks/${imageId}`);
        const unsubscribe = onValue(
          webhookRef,
          (snapshot) => {
            const data = snapshot.val();
            if (data && data.webhook_received) {
              const existingIndex = receivedUpdates.findIndex((u) => u.id === imageId);
              if (existingIndex > -1) {
                receivedUpdates[existingIndex] = {
                  id: imageId,
                  ...data
                };
              } else {
                receivedUpdates.push({
                  id: imageId,
                  ...data
                });
              }
              const completedImages = receivedUpdates.filter((u) => {
                const s = u.status?.toLowerCase();
                return !(s === "pending" || s === "processing");
              });
              console.log(`[RealtimeDB] Progress: ${completedImages.length}/${imageIds.length} completed`);
              const shouldEmit = completedImages.length >= Math.min(2, imageIds.length);
              const allCompleted = receivedUpdates.length === imageIds.length && completedImages.length === receivedUpdates.length;
              if (shouldEmit) {
                console.log(`[RealtimeDB] Emitting ${completedImages.length} completed images (all done: ${allCompleted})`);
                const results = receivedUpdates.map((update, index) => {
                  const originalRequest = originalRequests.find((req) => req.result.id === update.id);
                  let originalPrompt = "";
                  if (originalPrompts) {
                    const requestIndex = originalRequest?.request_index || index + 1;
                    if (typeof originalPrompts === "string") {
                      originalPrompt = originalPrompts;
                    } else if (Array.isArray(originalPrompts)) {
                      originalPrompt = originalPrompts[requestIndex - 1] || "";
                    } else if (originalPrompts[`prompt_${requestIndex}`]) {
                      originalPrompt = originalPrompts[`prompt_${requestIndex}`];
                    }
                  }
                  const s = update.status?.toLowerCase();
                  const isError = s === "failed" || s === "error";
                  const isModerated = s === "request moderated";
                  const isTransient = s === "pending" || s === "processing";
                  return {
                    id: update.id,
                    status: isModerated ? "Moderated" : isError ? s === "error" ? "Error" : "Failed" : isTransient ? "Pending" : "Ready",
                    progress: 100,
                    // Webhook means it's complete
                    result: update.result ? {
                      sample: update.result.sample || update.image_url,
                      prompt: originalPrompt || update.result.prompt || update.prompt || ""
                    } : null,
                    request_index: originalRequest?.request_index || index + 1
                  };
                });
                const completedResults = results.filter(
                  (r) => r.status === "Ready" || r.status === "Error" || r.status === "Failed" || r.status === "Moderated"
                );
                console.log(`[RealtimeDB] Converted ${completedResults.length} completed webhook updates to BflImageResult format`);
                subscriber.next(completedResults);
                if (allCompleted) {
                  console.log(`[RealtimeDB] All images completed, completing Observable (cleanup will be manual)`);
                  subscriber.complete();
                }
              }
            }
          },
          (error) => {
            console.error(`[RealtimeDB] Error listening for ${imageId}:`, error);
            subscriber.error(error);
          }
        );
        listeners.push(unsubscribe);
      });
      return () => {
        console.log(`[RealtimeDB] Cleaning up listeners for ${imageIds.length} image IDs`);
        listeners.forEach((unsubscribe) => unsubscribe());
      };
    }).pipe(
      timeout(12e4)
      // 2 minutes
    );
  }
  /** Listen for a single webhook update. */
  listenForSingleWebhook$(imageId) {
    return new Observable((subscriber) => {
      const webhookRef = ref(this.database, `/bfl-webhooks/${imageId}`);
      console.log(`[RealtimeDB] Setting up listener for single image ID: ${imageId}`);
      const unsubscribe = onValue(
        webhookRef,
        (snapshot) => {
          const data = snapshot.val();
          if (data && data.webhook_received) {
            console.log(`[RealtimeDB] Received webhook data for ${imageId}:`, data);
            subscriber.next({
              id: imageId,
              ...data
            });
            subscriber.complete();
          }
        },
        (error) => {
          console.error(`[RealtimeDB] Error listening for ${imageId}:`, error);
          subscriber.error(error);
        }
      );
      return () => {
        console.log(`[RealtimeDB] Cleaning up listener for ${imageId}`);
        unsubscribe();
      };
    }).pipe(
      timeout(3e5)
      // 5 minute timeout
    );
  }
  /** Get current webhook data (one-time read). */
  async getWebhookData(imageId) {
    return new Promise((resolve) => {
      const webhookRef = ref(this.database, `/bfl-webhooks/${imageId}`);
      onValue(
        webhookRef,
        (snapshot) => {
          const data = snapshot.val();
          resolve(data);
        },
        {
          onlyOnce: true
        }
      );
    });
  }
  /**
   * Clean up webhook entries from RTDB after processing to prevent accumulation.
   * Private method used internally.
   */
  async cleanupWebhookEntries(imageIds) {
    try {
      const db = getDatabase();
      const cleanupPromises = imageIds.map((imageId) => {
        const webhookRef = ref(db, `/bfl-webhooks/${imageId}`);
        return remove(webhookRef);
      });
      await Promise.all(cleanupPromises);
      console.log(`[RealtimeDB] Cleaned up ${imageIds.length} webhook entries`);
    } catch (error) {
      console.error(`[RealtimeDB] Error cleaning up webhook entries:`, error);
    }
  }
  /**
   * Public method to manually cleanup webhooks for specific images.
   * Called by components/services when they're done with the image generation process.
   */
  async cleanupWebhooksForImages(imageIds) {
    return this.cleanupWebhookEntries(imageIds);
  }
  /**
   * Clean up webhook entries older than 20 minutes on instantiation.
   * Uses local filtering to avoid Firebase indexing requirements.
   */
  async cleanupOldWebhookEntries() {
    try {
      const db = getDatabase();
      const webhooksRef = ref(db, "/bfl-webhooks");
      const twentyMinutesAgo = Date.now() - 10 * 60 * 1e3;
      const snapshot = await get(webhooksRef);
      if (snapshot.exists()) {
        const allEntries = snapshot.val();
        const entriesToDelete = [];
        Object.keys(allEntries).forEach((imageId) => {
          const entry = allEntries[imageId];
          if (entry.timestamp && entry.timestamp < twentyMinutesAgo) {
            entriesToDelete.push(imageId);
          }
        });
        if (entriesToDelete.length > 0) {
          console.log(`[RealtimeDB] Found ${entriesToDelete.length} old webhook entries to cleanup`);
          const deletePromises = entriesToDelete.map((imageId) => {
            const entryRef = ref(db, `/bfl-webhooks/${imageId}`);
            return remove(entryRef);
          });
          await Promise.all(deletePromises);
          console.log(`[RealtimeDB] Cleaned up ${entriesToDelete.length} old webhook entries (>20 min)`);
        } else {
          console.log(`[RealtimeDB] No old webhook entries found to cleanup`);
        }
      } else {
        console.log(`[RealtimeDB] No webhook entries found in database`);
      }
    } catch (error) {
      console.error(`[RealtimeDB] Error cleaning up old webhook entries:`, error);
    }
  }
};

// src/token.ts
var TokenService = class _TokenService {
  static instance;
  currentToken = null;
  unsubscribe = null;
  rtdb = RealtimeDatabaseService.getInstance();
  initPromise = null;
  constructor() {
  }
  static getInstance() {
    if (!_TokenService.instance) {
      _TokenService.instance = new _TokenService();
    }
    return _TokenService.instance;
  }
  /** Initialize RTDB listener and wait for first token (call once on app startup). */
  initialize() {
    if (this.initPromise) {
      return this.initPromise;
    }
    this.initPromise = new Promise((resolve, reject) => {
      let resolved = false;
      const timeout2 = setTimeout(() => {
        if (!resolved) {
          console.error("\u274C TokenService initialization timeout - no valid token received from RTDB after 10 seconds");
          console.error("Check that backend is running and has written a token to RTDB at: system/tokens/google-cloud");
          reject(new Error("TokenService initialization timeout"));
        }
      }, 1e4);
      this.unsubscribe = this.rtdb.listenToData("system/tokens/google-cloud", (data) => {
        this.currentToken = data;
        if (!resolved) {
          if (data?.token) {
            resolved = true;
            clearTimeout(timeout2);
            resolve();
          } else {
            console.warn("\u26A0\uFE0F TokenService received null/invalid token from RTDB, waiting for update...");
          }
        }
      });
    });
    return this.initPromise;
  }
  /** Get current token synchronously. */
  getToken() {
    if (!this.currentToken?.token) {
      console.error("\u274C currentToken is:", this.currentToken);
      throw new Error("Token not loaded yet - initialize() must be called first");
    }
    return this.currentToken.token;
  }
  /** Cleanup on app shutdown. */
  cleanup() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }
};

export { FirebaseService, LogLevel, RealtimeDatabaseService, TokenService, componentLogger, contextLogger, getDb, getFirebaseAuth, initFirebase, logger, serviceLogger };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map